import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { sendDirectorMessage } from '../../services/api';

const DirectorBlogPage = () => {
    const { currentLanguage } = useLanguage();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Тексты на разных языках
    const texts = {
        ru: {
            title: "Уважаемые читатели!",
            paragraph1: "Для меня чрезвычайно важно установить с вами прямую и открытую связь. В этой связи на официальном сайте Национальной библиотеки Республики Казахстан мы запустили специальный раздел — «Блог директора». Через данный ресурс вы можете напрямую обратиться ко мне с интересующими вас вопросами и предложениями.",
            paragraph2: "Каждое поступившее обращение помогает нам совершенствовать деятельность библиотеки, внедрять новые идеи и реализовывать актуальные инициативы. Ваши мнения относительно библиотечного фонда, качества обслуживания, а также культурных и научных проектов представляют для нас особую ценность.",
            paragraph3: "Со своей стороны я готова открыто и аргументированно отвечать на ваши вопросы, а также выстраивать конструктивный диалог. Национальная библиотека — это не только хранилище знаний и культуры, но и современный центр, работающий в духовной гармонии со своими читателями. Надеюсь на ваше активное участие и сотрудничество во имя общего развития!",
            directorName: "Нургалиева Газиза Кудайбергеновна",
            directorPosition: "Директор Национальной библиотеки Республики Казахстан",
            formTitle: "Написать директору",
            formSubtitle: "Поделитесь своими мыслями, идеями или задайте вопрос",
            nameLabel: "Ваше имя *",
            emailLabel: "Email *",
            subjectLabel: "Тема сообщения *",
            messageLabel: "Сообщение *",
            namePlaceholder: "Введите ваше имя",
            emailPlaceholder: "ваш@email.com",
            subjectPlaceholder: "О чем хотите написать?",
            messagePlaceholder: "Напишите ваше сообщение...",
            submitButton: "Отправить сообщение",
            submittingButton: "Отправляется...",
            signature: "С уважением,",
            validationError: "Пожалуйста, заполните все обязательные поля",
            successMessage: "Сообщение успешно отправлено!",
            errorMessage: "Произошла ошибка при отправке сообщения"
        },
        kz: {
            title: "Құрметті оқырман!",
            paragraph1: "Сіздермен тікелей әрі ашық байланыс орнату – мен үшін аса маңызды. Осы мақсатта Қазақстан Республикасының Ұлттық кітапханасының ресми сайтында арнайы «Директор блогы» айдарын іске қостық. Бұл бөлім арқылы Сіздер маған тікелей жолдама жасап, өздеріңізді толғандырған сұрақтар мен ұсыныстарыңызды жеткізе аласыздар.",
            paragraph2: "Сіздерден келіп түскен әрбір сауал – кітапхананың қызметін жетілдіруге, жаңа идеялар мен тың бастамаларды жүзеге асыруға бағыт-бағдар береді деп білемін. Кітапхана қорына, қызмет көрсету сапасына, мәдени және ғылыми жобаларға қатысты ой-пікірлеріңіз біз үшін аса құнды.",
            paragraph3: "Мен өз тарапымнан барлық сұрақтарға ашық, негізделген жауап беруге және Сіздермен сындарлы диалог орнатуға дайынмын. Ұлттық кітапхана – тек білім мен мәдениеттің ордасы ғана емес, сонымен қатар оқырманмен рухани үндестікте жұмыс істейтін заманауи орталық. Сіздердің белсенді қатысуларыңызға және ортақ даму жолындағы ынтымақтастығымызға сенім білдіремін!",
            directorName: "Нұрғалиева Ғазиза Құдайбергенқызы",
            directorPosition: "Қазақстан Республикасының Ұлттық кітапханасының директоры",
            formTitle: "Директорға жазу",
            formSubtitle: "Ойларыңызбен, идеяларыңызбен бөлісіңіз немесе сұрақ қойыңыз",
            nameLabel: "Сіздің атыңыз *",
            emailLabel: "Email *",
            subjectLabel: "Хабарлама тақырыбы *",
            messageLabel: "Хабарлама *",
            namePlaceholder: "Атыңызды енгізіңіз",
            emailPlaceholder: "сіздің@email.com",
            subjectPlaceholder: "Не туралы жазғыңыз келеді?",
            messagePlaceholder: "Хабарламаңызды жазыңыз...",
            submitButton: "Хабарлама жіберу",
            submittingButton: "Жіберілуде...",
            signature: "Құрметпен,",
            validationError: "Барлық міндетті өрістерді толтырыңыз",
            successMessage: "Хабарлама сәтті жіберілді!",
            errorMessage: "Хабарлама жіберу кезінде қате орын алды"
        },
        en: {
            title: "Dear Readers,",
            paragraph1: "Establishing a direct and open line of communication with you is of great importance to me. To this end, we have launched a special section — the \"Director's Blog\" — on the official website of the National Library of the Republic of Kazakhstan. Through this platform, you can reach out to me personally with your questions and suggestions.",
            paragraph2: "Each message we receive is a valuable contribution that helps us improve the library's services, implement new ideas, and realize relevant initiatives. Your feedback regarding our collections, service quality, as well as cultural and scientific projects is of particular value to us.",
            paragraph3: "I am committed to responding openly and substantively to your questions and to fostering a constructive dialogue. The National Library is not only a repository of knowledge and culture but also a modern center working in close alignment with its readers' values and needs. I look forward to your active participation and cooperation for our shared development!",
            directorName: "Nurgalieva Gaziza Kudaybergenovna",
            directorPosition: "Director of the National Library of the Republic of Kazakhstan",
            formTitle: "Write to the Director",
            formSubtitle: "Share your thoughts, ideas or ask a question",
            nameLabel: "Your Name *",
            emailLabel: "Email *",
            subjectLabel: "Message Subject *",
            messageLabel: "Message *",
            namePlaceholder: "Enter your name",
            emailPlaceholder: "your@email.com",
            subjectPlaceholder: "What would you like to write about?",
            messagePlaceholder: "Write your message...",
            submitButton: "Send Message",
            submittingButton: "Sending...",
            signature: "Sincerely,",
            validationError: "Please fill in all required fields",
            successMessage: "Message sent successfully!",
            errorMessage: "An error occurred while sending the message"
        }
    };

    const currentTexts = texts[currentLanguage] || texts.ru;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        // Валидация полей
        if (!formData.name || !formData.email || !formData.subject || !formData.message) {
            alert(currentTexts.validationError);
            return;
        }

        setIsSubmitting(true);

        try {
            // Отправка данных через api функцию
            await sendDirectorMessage({
                name: formData.name,
                email: formData.email,
                subject: formData.subject,
                message: formData.message,
                language: currentLanguage
            });

            alert(currentTexts.successMessage);
            setFormData({ name: '', email: '', subject: '', message: '' });
        } catch (error) {
            console.error('Error sending message:', error);
            alert(currentTexts.errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <div className="bg-white shadow-sm">
                <div className="max-w-4xl mx-auto py-8 sm:py-16 px-4 sm:px-6">
                    <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                        <div className="flex-1 order-2 lg:order-1">
                            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6 text-center lg:text-left">
                                {currentTexts.title}
                            </h1>
                            <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-700 leading-relaxed">
                                <p>{currentTexts.paragraph1}</p>
                                <p>{currentTexts.paragraph2}</p>
                                <p>{currentTexts.paragraph3}</p>
                            </div>
                        </div>
                        <div className="flex-shrink-0 order-1 lg:order-2">
                            <div className="relative">
                                <div className="w-64 sm:w-72 lg:w-80 h-72 sm:h-80 lg:h-96 bg-gradient-to-b from-gray-200 to-gray-300 rounded-lg overflow-hidden shadow-lg">
                                    <img
                                        src="/assets/images/director.jpg"
                                        alt={currentTexts.directorName}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Director Info Section */}
            <div className="bg-white border-t">
                <div className="max-w-4xl mx-auto py-8 sm:py-12 px-4 sm:px-6">
                    <div className="text-center">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                            {currentTexts.directorName}
                        </h2>
                        <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 px-4">
                            {currentTexts.directorPosition}
                        </p>

                        <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6 text-gray-500 text-sm sm:text-base">
                            <div className="flex items-center space-x-2">
                                <Mail className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                                <span className="break-all">org.nbrk@mail.kz</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Phone className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                                <span>+7 (727) 267-28-83 </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                                <span>Алматы, Казахстан</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contact Form Section */}
            <div className="bg-gray-50 py-12 sm:py-16">
                <div className="max-w-2xl mx-auto px-4 sm:px-6">
                    <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
                            {currentTexts.formTitle}
                        </h3>
                        <p className="text-gray-600 text-center mb-6 sm:mb-8 text-sm sm:text-base px-4">
                            {currentTexts.formSubtitle}
                        </p>

                        <div className="space-y-4 sm:space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                        {currentTexts.nameLabel}
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        disabled={isSubmitting}
                                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none focus:border-transparent transition-colors text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        style={{ '--tw-ring-color': 'var(--primary-color)' }}
                                        placeholder={currentTexts.namePlaceholder}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                        {currentTexts.emailLabel}
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        disabled={isSubmitting}
                                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none focus:border-transparent transition-colors text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        style={{ '--tw-ring-color': 'var(--primary-color)' }}
                                        placeholder={currentTexts.emailPlaceholder}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                                    {currentTexts.subjectLabel}
                                </label>
                                <input
                                    type="text"
                                    id="subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    disabled={isSubmitting}
                                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none focus:border-transparent transition-colors text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    style={{ '--tw-ring-color': 'var(--primary-color)' }}
                                    placeholder={currentTexts.subjectPlaceholder}
                                />
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                                    {currentTexts.messageLabel}
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows={6}
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    disabled={isSubmitting}
                                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none focus:border-transparent transition-colors text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    style={{ '--tw-ring-color': 'var(--primary-color)' }}
                                    placeholder={currentTexts.messagePlaceholder}
                                />
                            </div>

                            <div className="text-center">
                                <button
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    className="inline-flex items-center px-6 sm:px-8 py-2 sm:py-3 bg-[var(--primary-color)] text-white font-medium rounded-lg transition-colors border-2 border-transparent hover:bg-white hover:text-[var(--primary-color)] hover:border-[var(--primary-color)] focus:ring-2 focus:ring-[var(--primary-color)] focus:ring-offset-2 text-sm sm:text-base disabled:bg-opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Send className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                                    {isSubmitting ? currentTexts.submittingButton : currentTexts.submitButton}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Additional Info Section */}
            <div className="bg-white py-8 sm:py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <div className="text-center">
                        <p className="text-gray-600 text-base sm:text-lg">
                            {currentTexts.signature}
                        </p>
                        <p className="text-lg sm:text-xl font-semibold text-gray-900 mt-2">
                            {currentTexts.directorName}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DirectorBlogPage;